import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, errMsg, mediaUrl } from "@/lib/api";

const RadioCtx = createContext(null);

function fadeAudioTo(audioElement, target, duration, timerRef) {
  if (timerRef.current) clearInterval(timerRef.current);

  return new Promise((resolve) => {
    const startVolume = audioElement.volume;
    const startedAt = Date.now();

    if (duration <= 0) {
      audioElement.volume = target;
      timerRef.current = null;
      resolve();
      return;
    }

    timerRef.current = setInterval(() => {
      const progress = Math.min(1, (Date.now() - startedAt) / duration);
      audioElement.volume = Math.max(
        0,
        Math.min(1, startVolume + (target - startVolume) * progress)
      );

      if (progress >= 1) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        resolve();
      }
    }, 20);
  });
}

// Client-side transport for the server-side per-business programming engine.
export function RadioProvider({ businessId = null, children, enableAdOverlap = false }) {
  const audio = useRef(null);
  const overlayAudio = useRef(null);
  const itemRef = useRef(null);
  const mainItemRef = useRef(null);
  const overlayActiveRef = useRef(false);
  const overlayTriggeredRef = useRef(false);
  const mainEndedRef = useRef(false);
  const pendingNextRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const [item, setItem] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [conn, setConn] = useState("idle"); // idle | connecting | live | buffering | reconnecting | error
  const [error, setError] = useState("");
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem("mk_vol");
    return saved === null ? 0.8 : Math.max(0, Math.min(1, Number(saved)));
  });
  const [adVolume, setAdVolume] = useState(() => {
    const saved = localStorage.getItem("mk_ad_vol");
    return saved === null ? 0.8 : Math.max(0, Math.min(1, Number(saved)));
  });
  const [progress, setProgress] = useState(0);
  const wantPlay = useRef(false);
  const retries = useRef(0);
  const lastPresenceAt = useRef(0);

  useEffect(() => {
    const a = new Audio();
    a.preload = "auto";
    a.crossOrigin = "use-credentials";
    audio.current = a;

    const overlay = new Audio();
    overlay.preload = "auto";
    overlay.crossOrigin = "use-credentials";
    overlayAudio.current = overlay;

    return () => {
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
      a.pause();
      a.src = "";
      overlay.pause();
      overlay.src = "";
    };
  }, []);

  useEffect(() => {
    if (audio.current) {
      const current = itemRef.current;
      audio.current.volume = current?.type === "ad"
        ? adVolume
        : overlayActiveRef.current
          ? volume * 0.10
          : volume;
    }
    if (overlayAudio.current) overlayAudio.current.volume = adVolume;
    localStorage.setItem("mk_vol", String(volume));
    localStorage.setItem("mk_ad_vol", String(adVolume));
  }, [volume, adVolume]);

  useEffect(() => {
    const sendPresence = (force = false) => {
      const timestamp = Date.now();
      if (!force && timestamp - lastPresenceAt.current < 15000) return;
      lastPresenceAt.current = timestamp;
      const current = playing && item ? item : null;
      api.post("/radio/presence", {
        playing: Boolean(current),
        connection: conn,
        current_id: current?.id || null,
        current_type: current?.type || null,
        current_title: current?.title || "",
        current_artist: current?.artist || "",
        current_duration: Number(current?.duration) || 0,
        current_started_at: current?.started_at || null,
      }).catch(() => {});
    };

    const onPlaybackProgress = () => sendPresence();
    sendPresence(true);
    const timer = window.setInterval(() => sendPresence(), 15000);
    const player = audio.current;
    player?.addEventListener("timeupdate", onPlaybackProgress);

    return () => {
      window.clearInterval(timer);
      player?.removeEventListener("timeupdate", onPlaybackProgress);
    };
  }, [item, playing, conn]);

  const report = useCallback((type, message) => {
    api.post("/radio/event", { type, message, business_id: businessId }).catch(() => {});
  }, [businessId]);

  const loadNext = useCallback(async () => {
    const a = audio.current;
    if (!a || !wantPlay.current) return;
    setConn((c) => (c === "live" ? "buffering" : c === "reconnecting" ? c : "connecting"));
    setPlaying(false);
    try {
      let data = pendingNextRef.current;
      pendingNextRef.current = null;

      if (!data) {
        const response = await api.post("/radio/next", null, {
          params: businessId ? { business_id: businessId } : {},
        });
        data = response.data;
      }

      if (!wantPlay.current) return;

      itemRef.current = data;
      mainItemRef.current = data;
      overlayTriggeredRef.current = false;
      mainEndedRef.current = false;
      overlayActiveRef.current = false;

      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;

      if (overlayAudio.current) overlayAudio.current.pause();
      a.volume = data.type === "ad" ? adVolume : volume;
      setItem(data);
      setError("");
      a.src = mediaUrl(data.media_url);
      await a.play();
      retries.current = 0;
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: data.title, artist: data.type === "ad" ? `Anúncio • ${data.artist}` : data.artist, album: "MarkImagem Rádio Web",
        });
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      if (status === 409 && typeof detail === "string" && detail.startsWith("WAITING_TRACKS_3H:")) {
        setConn("reconnecting");
        setPlaying(false);
        setError("Aguardando uma música ficar disponível. Nova tentativa em 60 segundos…");
        setTimeout(() => {
          if (wantPlay.current) loadNext();
        }, 60000);
        return;
      }

      if (status === 402 || status === 409 || status === 403 || status === 400) {
        wantPlay.current = false;
        setPlaying(false);
        setConn("error");
        setError(errMsg(e));
        return;
      }
      retries.current += 1;
      setConn("reconnecting");
      setError(navigator.onLine ? "Instabilidade na conexão. Reconectando…" : "Sem conexão com a internet. Aguardando retorno…");
      report("error", `next: ${e?.message || status}`);
      setTimeout(() => loadNext(), Math.min(15000, 1500 * retries.current));
    }
  }, [businessId, report, volume, adVolume]);

  useEffect(() => {
    const a = audio.current;
    const overlay = overlayAudio.current;
    if (!a || !overlay) return undefined;

    const onEnded = () => {
      if (overlayActiveRef.current) {
        mainEndedRef.current = true;
        return;
      }
      loadNext();
    };

    const onOverlayEnded = () => {
      overlayActiveRef.current = false;
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;

      // O anúncio e a música terminam juntos: interrompe eventual sobra da música
      // e avança imediatamente para a próxima faixa da programação.
      mainEndedRef.current = false;
      a.pause();
      loadNext();
    };

    const onPlaying = () => { setConn("live"); setPlaying(true); setError(""); };
    const onWaiting = () => { setConn("buffering"); setPlaying(false); };

    const onTime = () => {
      setProgress(a.duration ? a.currentTime / a.duration : 0);

      const current = itemRef.current;

      // Limite de quatro minutos para músicas mais longas.
      const hasFourMinuteLimit =
        current?.type === "music" &&
        Number.isFinite(a.duration) &&
        a.duration > 240;

      if (hasFourMinuteLimit && a.currentTime >= 240) {
        a.pause();
        setProgress(1);

        if (overlayActiveRef.current) {
          // A locução continua; a música já terminou.
          mainEndedRef.current = true;
        } else {
          // Sem locução ativa, segue diretamente para a próxima faixa.
          loadNext();
        }
        return;
      }

      // Para músicas longas, a janela do anúncio termina aos 4 minutos.
      const adWindowEnd = hasFourMinuteLimit ? 240 : a.duration;

      if (
        !enableAdOverlap ||
        !wantPlay.current ||
        !current ||
        current.type !== "music" ||
        overlayTriggeredRef.current ||
        !Number.isFinite(a.duration) ||
        adWindowEnd - a.currentTime > 15
      ) return;

      overlayTriggeredRef.current = true;

      (async () => {
        try {
          const params = businessId ? { business_id: businessId } : {};
          const due = await api.get("/radio/ad-due", { params });
          if (!due.data?.due || !wantPlay.current || itemRef.current !== current) return;

          const response = await api.post("/radio/next", null, { params });
          const ad = response.data;

          if (!wantPlay.current || itemRef.current !== current) return;

          if (ad.type !== "ad") {
            // Preserve the returned item for the normal transition.
            pendingNextRef.current = ad;
            return;
          }

          overlayActiveRef.current = true;

          // Reduz a música em 1 segundo antes de iniciar a locução.
          await fadeAudioTo(a, volume * 0.10, 1000, fadeTimerRef);

          // Se a reprodução foi pausada ou a faixa mudou durante o fade,
          // não inicia um anúncio fora de hora.
          if (!wantPlay.current || itemRef.current !== current) {
            if (itemRef.current === current) {
              overlayActiveRef.current = false;
              a.volume = volume;
            }
            return;
          }

          overlay.volume = adVolume;
          setItem(ad);
          overlay.src = mediaUrl(ad.media_url);
          await overlay.play();
        } catch (e) {
          // Falha no anúncio: restaura a música e não deixa a rádio travada.
          overlayActiveRef.current = false;
          if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
          fadeTimerRef.current = null;
          overlay.pause();
          overlay.src = "";
          a.volume = mainItemRef.current?.type === "ad" ? adVolume : volume;
          setItem(mainItemRef.current);
          report("error", `ad overlap: ${e?.message || "falha ao iniciar anúncio"}`);

          if (mainEndedRef.current) {
            mainEndedRef.current = false;
            loadNext();
          }
        }
      })();
    };
    const onErr = () => {
      if (!wantPlay.current) return;
      setPlaying(false);
      report("error", `media error ${a.error?.code || ""}`);
      setConn("reconnecting");
      retries.current += 1;
      setTimeout(() => {
        if (!wantPlay.current) return;
        const t = a.currentTime;
        if (retries.current <= 2 && a.src) {
          a.load();
          a.currentTime = t;
          a.play().catch(() => loadNext());
        } else loadNext();
      }, 1500);
    };
    const onOnline = () => {
      if (wantPlay.current && a.paused) {
        report("reconnect", "online");
        a.play().catch(() => loadNext());
      }
    };
    a.addEventListener("ended", onEnded);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("error", onErr);
    overlay.addEventListener("ended", onOverlayEnded);
    window.addEventListener("online", onOnline);
    return () => {
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("error", onErr);
      overlay.removeEventListener("ended", onOverlayEnded);
      window.removeEventListener("online", onOnline);
    };
  }, [loadNext, report, volume, adVolume, businessId, enableAdOverlap]);

  const play = async () => {
    wantPlay.current = true;
    setError("");
    const a = audio.current;
    if (a.src && item) {
      setConn("buffering");
      try {
        await a.play();
        if (overlayActiveRef.current) await overlayAudio.current?.play();
        return;
      } catch {
        /* fall through to next item */
      }
    }
    loadNext();
  };
  const pause = () => {
    wantPlay.current = false;
    audio.current?.pause();
    overlayAudio.current?.pause();
    setPlaying(false);
    setConn("idle");
  };
  const toggle = () => (playing || conn === "connecting" || conn === "buffering" ? pause() : play());

  return (
    <RadioCtx.Provider value={{ item, playing, conn, error, volume, setVolume, adVolume, setAdVolume, enableAdOverlap, toggle, play, pause, progress }}>
      {children}
    </RadioCtx.Provider>
  );
}

export const useRadio = () => useContext(RadioCtx);
